import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { HERO } from "@/constants/testIds";
import { ArrowUpRight, MoveDown } from "lucide-react";

const REVEAL = {
  hidden: { y: "110%" },
  visible: (i = 0) => ({
    y: "0%",
    transition: {
      delay: 0.15 + i * 0.12,
      duration: 1.2,
      ease: [0.76, 0, 0.24, 1],
    },
  }),
};

export default function Hero({ onOpenGallery }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 8]);

  return (
    <section
      ref={ref}
      data-testid={HERO.root}
      className="relative min-h-[100svh] pt-24 md:pt-28 pb-16 px-4 md:px-8 lg:px-12 overflow-hidden"
    >
      {/* Grid guides */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0A0A0A 1px, transparent 1px)",
          backgroundSize: "8.333% 100%",
        }}
      />

      {/* Meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="flex items-center justify-between font-mono-caps text-[10px] md:text-xs mb-6 md:mb-10"
      >
        <span>[001 / DROP 2025]</span>
        <span className="hidden md:inline">Made in Greece · Printed on Demand</span>
        <span>Ν. 42.5° / Α. 23.7°</span>
      </motion.div>

      {/* Kinetic display */}
      <div className="relative">
        <h1 className="font-display leading-[0.85] text-[16vw] md:text-[13vw] lg:text-[11vw]">
          <span className="reveal-mask block">
            <motion.span
              variants={REVEAL}
              initial="hidden"
              animate="visible"
              custom={0}
              className="block"
            >
              ΠΡΩΤΟΤΥΠΑ ΣΧΕΔΙΑ
            </motion.span>
          </span>
          <span className="reveal-mask block">
            <motion.span
              variants={REVEAL}
              initial="hidden"
              animate="visible"
              custom={1}
              className="block flex items-baseline gap-3 md:gap-6"
            >
              <span className="text-outline">STREET</span>
              <span className="inline-block w-[0.4em] h-[0.4em] rounded-full bg-[var(--lime)] translate-y-[-0.15em]" />
              <span>WEAR</span>
            </motion.span>
          </span>
          <span className="reveal-mask block">
            <motion.span
              variants={REVEAL}
              initial="hidden"
              animate="visible"
              custom={2}
              className="block"
            >
              ΓΙΑ ΟΛΟΥΣ<span className="text-[var(--pink)]">.</span>
            </motion.span>
          </span>
        </h1>

        {/* Floating parallax badge */}
        <motion.div
          style={{ y, scale, rotate }}
          className="absolute -right-4 md:right-4 -top-6 md:top-6 pointer-events-none select-none"
          aria-hidden
        >
          <div className="relative w-32 h-32 md:w-52 md:h-52 rounded-full bg-[var(--lime)] border border-[var(--ink)] flex items-center justify-center">
            <svg viewBox="0 0 200 200" className="w-full h-full absolute inset-0 animate-[spin_18s_linear_infinite]">
              <defs>
                <path id="circlePath" d="M 100,100 m -78,0 a 78,78 0 1,1 156,0 a 78,78 0 1,1 -156,0" />
              </defs>
              <text className="font-mono-caps" fontSize="14" fill="#0A0A0A" letterSpacing="4">
                <textPath href="#circlePath">BLOUZES4ALL · ORIGINAL PRINTS · MADE IN GR · </textPath>
              </text>
            </svg>
            <div className="font-display text-3xl md:text-5xl">10</div>
          </div>
        </motion.div>
      </div>

      {/* Sub row */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end"
      >
        <p className="md:col-span-5 text-base md:text-lg leading-relaxed max-w-md">
          Δέκα φρέσκα σχέδια, τυπωμένα με μεράκι σε
          βαμβακερά μπλουζάκια. Διάλεξε χρώμα, ποσότητα και στείλε την
          παραγγελία σου σε ένα κλικ — έρχεται κατευθείαν στο mail μας.
        </p>
        <div className="md:col-span-4 flex flex-wrap items-center gap-4">
          <button
            data-testid={HERO.ctaShop}
            onClick={onOpenGallery}
            className="btn-lime"
            data-cursor="hover"
          >
            ΔΕΣ ΤΗ ΣΥΛΛΟΓΗ <ArrowUpRight className="w-4 h-4" />
          </button>
          <a
            href="/epikoinonia"
            data-testid={HERO.ctaContact}
            className="btn-ghost"
            data-cursor="hover"
          >
            ΕΠΙΚΟΙΝΩΝΙΑ
          </a>
        </div>
        <div className="md:col-span-3 md:text-right font-mono-caps text-[10px] md:text-xs">
          <div className="flex md:justify-end items-center gap-2">
            <MoveDown className="w-3 h-3" /> SCROLL
          </div>
          <div className="mt-1 opacity-60">SIGNATURE DROP · Νο. 001</div>
        </div>
      </motion.div>
    </section>
  );
}
